namespace $ {
	
	const wrappers = new WeakMap< Promise< unknown >, Promise< any > >()
	
	/**
	 * Suspendable task with support both sync/async api.
	 * 
	 * 	A1 A2 A3 A4 P1 P2 P3 P4 S1 S2 S3
	 * 	^           ^           ^
	 * 	args_from   pubs_from   subs_from
	 **/
	export abstract class $mol_wire_fiber<
		Host,
		Args extends readonly unknown[],
		Result,
	> extends $mol_wire_pub_sub {
	
		static warm = true
		
		static planning = new Set< $mol_wire_fiber< any, any, any > >()
		static reaping = new Set< $mol_wire_fiber< any, any, any > >()
		
		static plan_task: $mol_after_tick | null = null
		static plan() {
			
			if( this.plan_task ) return
			$mol_wire_trace( 'plan_tick', '$mol_wire_fiber', {
				planning: this.planning.size,
				reaping: this.reaping.size,
			} )
			
			this.plan_task = new $mol_after_tick( ()=> {
				
				try {
					this.sync()
				} finally {
					$mol_wire_fiber.plan_task = null
				}

			} )
			
		}
		
		static sync() {
			$mol_wire_trace( 'sync_graph_start', '$mol_wire_fiber', {
				planning: this.planning.size,
				reaping: this.reaping.size,
			} )
			
			// Sync whole fiber graph
			while( this.planning.size ) {
				for( const fiber of this.planning ) {
					this.planning.delete( fiber )
					$mol_wire_trace( 'planned_fresh', fiber[ Symbol.toStringTag ], {
						cursor: fiber.cursor,
					} )
					if( fiber.cursor >= 0 ) continue
					if( fiber.cursor === $mol_wire_cursor.final ) continue
					fiber.fresh()
				}
			}
			
			// Collect garbage
			while( this.reaping.size ) {
				
				const fibers = this.reaping
				this.reaping = new Set
				
				for( const fiber of fibers ) {
					if( !fiber.sub_empty ) continue
					fiber.destructor()
				}
				
			}
			$mol_wire_trace( 'sync_graph_end', '$mol_wire_fiber', {
				planning: this.planning.size,
				reaping: this.reaping.size,
			} )
			
		}
		
		public cache: Result | Error | Promise< Result | Error > = undefined as any
		
		get args() {
			return this.data.slice( 0 , this.pub_from ) as any as Args
		}
		
		result() {
			if( $mol_promise_like( this.cache ) ) return
			if( this.cache instanceof Error ) return
			return this.cache
		}
		
		get incompleted() {
			return $mol_promise_like( this.cache )
		}
		
		field() {
			return this.task.name + '()'
		}
		
		constructor(
			id: string,
			readonly task: ( this : Host , ... args : Args )=> Result,
			readonly host?: Host,
			args?: Args
		) {
			
			super( id )
			if( args ) this.data.push( ... args )
			this.pub_from = this.sub_from = args?.length ?? 0
			
		}
		
		plan() {
			$mol_wire_fiber.planning.add( this )
			$mol_wire_trace( 'plan', this[ Symbol.toStringTag ], {
				cursor: this.cursor,
				subs: ( this.data.length - this.sub_from ) / 2,
			} )
			$mol_wire_fiber.plan()
			return this
		}
		
		reap() {
			$mol_wire_fiber.reaping.add( this )
			$mol_wire_fiber.plan()
		}
		
		toString() {
			return this[ Symbol.toStringTag ]
		}
		
		toJSON() {
			return this[ Symbol.toStringTag ]
		}

		[ $mol_dev_format_head ]() {
			
			const cursor = {
				[ $mol_wire_cursor.stale ]: '🔴',
				[ $mol_wire_cursor.doubt ]: '🟡',
				[ $mol_wire_cursor.fresh ]: '🟢',
				[ $mol_wire_cursor.final ]: '🔵',
			}[ this.cursor ] ?? this.cursor.toString()
			
			return $mol_dev_format_div( {},
				$mol_owning_check( this, this.cache )
					? $mol_dev_format_shade( cursor )
					: $mol_dev_format_shade( this[ Symbol.toStringTag ], cursor ),
				$mol_dev_format_auto( this.cache ),
			)
			
		}
		[ $mol_dev_format_body ]() { return null }
		
		get $() {
			return ( this.host ?? this.task as any )['$']
		}
		
		emit( quant = $mol_wire_cursor.stale ) {
			$mol_wire_trace( 'fiber_emit', this[ Symbol.toStringTag ], {
				quant,
				subs_empty: this.sub_empty,
			} )
			if( this.sub_empty ) this.plan()
			else super.emit( quant )
		}
		
		fresh() {
			const trace = $mol_wire_trace_active( 'fresh_start', this[ Symbol.toStringTag ] )
			const time = trace ? Date.now() : 0
			if( trace ) {
				$mol_wire_trace( 'fresh_start', this[ Symbol.toStringTag ], {
					cursor: this.cursor,
					pubs: ( this.sub_from - this.pub_from ) / 2,
					subs: ( this.data.length - this.sub_from ) / 2,
				} )
			}

			type Result = typeof this.cache
			
			if( this.cursor === $mol_wire_cursor.fresh ) {
				if( trace ) $mol_wire_trace( 'fresh_skip', this[ Symbol.toStringTag ], { reason: 'fresh' } )
				return
			}
			if( this.cursor === $mol_wire_cursor.final ) {
				if( trace ) $mol_wire_trace( 'fresh_skip', this[ Symbol.toStringTag ], { reason: 'final' } )
				return
			}
			
			check: if( this.cursor === $mol_wire_cursor.doubt ) {
				
				for( let i = this.pub_from ; i < this.sub_from; i += 2 ) {
					;( this.data[i] as $mol_wire_pub )?.fresh()
					if( this.cursor !== $mol_wire_cursor.doubt ) break check
				}
				
				this.cursor = $mol_wire_cursor.fresh
				if( trace ) $mol_wire_trace( 'fresh_end', this[ Symbol.toStringTag ], {
					duration: Date.now() - time,
					result: 'doubt_fresh',
					pubs: ( this.sub_from - this.pub_from ) / 2,
				} )
				return
				
			}
			
			const bu = this.track_on()
			let result: typeof this.cache

			try {

				switch( this.pub_from ) {
					case 0: result = (this.task as any).call( this.host! ); break
					case 1: result = (this.task as any).call( this.host!, this.data[0] ); break
					default: result = (this.task as any).call( this.host!, ... this.args ); break
				}
				
				if( $mol_promise_like( result ) ) {
					if( trace ) $mol_wire_trace( 'fresh_promise', this[ Symbol.toStringTag ], {
						source: 'return',
					} )

					if( wrappers.has( result ) ) {
						result = wrappers.get( result )!.then(a=>a)
					} else {
						
						const put = ( res: Result )=> {
							if( this.cache === result ) this.put( res )
							return res
						}

						wrappers.set( result, result = Object.assign(
							result.then( put, put ),
							{ destructor: ( result as any ).destructor || (()=> {}) }
						) )
						wrappers.set( result, result )

						const error = new Error( `Promise in ${ this }` )
						Object.defineProperty( result, 'stack', { get: ()=> error.stack } )

					}
				}
				
			} catch( error: any ) {
				
				if( error instanceof Error || $mol_promise_like( error ) ) {
					result = error
				} else {
					result = new Error( String( error ), { cause: error } )
				}
				
				if( $mol_promise_like( result ) ) {
					if( trace ) $mol_wire_trace( 'fresh_promise', this[ Symbol.toStringTag ], {
						source: 'throw',
					} )
					
					if( wrappers.has( result ) ) {
						result = wrappers.get( result )!
					} else {
						
						const put = ( v: any )=> {
							if( this.cache === result ) this.absorb()
							return v
						}
						
						wrappers.set( result, result = Object.assign(
							result.then( put, put ),
							{ destructor: ( result as any ).destructor || (()=> {}) }
						) )
						
						const error = new Error( `Promise in ${ this }` )
						Object.defineProperty( result, 'stack', { get: ()=> error.stack } )

					}
					
				}
				
			}
			
			if( ! $mol_promise_like( result ) ) {
				this.track_cut()
			}
			
			this.track_off( bu )
			this.put( result )
			if( trace ) $mol_wire_trace( 'fresh_end', this[ Symbol.toStringTag ], {
				duration: Date.now() - time,
				result: $mol_promise_like( result )
					? 'promise'
					: result instanceof Error
						? 'error'
						: 'value',
				pubs: ( this.sub_from - this.pub_from ) / 2,
			} )
			
			return this
		}
		
		refresh() {
			this.cursor = $mol_wire_cursor.stale
			this.fresh()
		}
		
		abstract put( next: Result | Error | Promise< Result | Error > ): Result | Error | Promise< Result | Error >
		
		/**
		 * Synchronous execution. Throws Promise when waits async task (SuspenseAPI provider).
		 * Should be called inside SuspenseAPI consumer (ie fiber).
		 */
		sync() {
			$mol_wire_trace( 'sync_start', this[ Symbol.toStringTag ], {
				cursor: this.cursor,
			} )
			
			if( !$mol_wire_fiber.warm ) {
				return this.result() as Awaited< Result >
			}
			
			this.promote()
			this.fresh()
			
			if( this.cache instanceof Error ) {
				return $mol_fail_hidden( this.cache )
			}
			
			if( $mol_promise_like( this.cache ) ) {
				$mol_wire_trace( 'sync_promise', this[ Symbol.toStringTag ] )
				return $mol_fail_hidden( this.cache )
			}
			
			$mol_wire_trace( 'sync_end', this[ Symbol.toStringTag ] )
			return this.cache as Awaited< Result >
		}

		/**
		 * Asynchronous execution.
		 * It's SuspenseAPI consumer. So SuspenseAPI providers can be called inside.
		 */
		async async_raw() {
			
			while( true ) {
				
				this.fresh()
				
				if( this.cache instanceof Error ) {
					$mol_fail_hidden( this.cache )
				}
				
				if( ! $mol_promise_like( this.cache ) ) return this.cache
					
				await Promise.race([ this.cache, this.step() ])
				if( ! $mol_promise_like( this.cache ) ) return this.cache
					
				if( this.cursor === $mol_wire_cursor.final ) {
					// never ends on destructed fiber
					await new Promise( ()=> {} )
				}
				
			}
			
		}

		async() {
			const promise = this.async_raw() as Promise<Result> & { destructor(): void }

			if (! promise.destructor) promise.destructor = () => this.destructor()

			return promise
		}
		
		step() {
			return new Promise< null >( done => {
				const sub = new $mol_wire_pub_sub
				const prev = sub.track_on()
				sub.track_next( this )
				sub.track_off( prev )
				sub.absorb = ()=> {
					done( null )
					setTimeout( ()=> sub.destructor() )
				}
			} )
		}

		destructor() {
			
			super.destructor()
			
			$mol_wire_fiber.planning.delete( this )
			
			if( ! $mol_owning_check( this, this.cache ) ) return

			try {
				this.cache.destructor()
			} catch (result) {
				if ($mol_promise_like(result)) {
					const error = new Error( `Promise in ${ this }.destructor()` )
					Object.defineProperty( result, 'stack', { get: ()=> error.stack } )
				}

				$mol_fail_hidden(result)
			}

		}
		
	}
	
}
